import { useParams } from 'next/navigation';
import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { useUpdateEffect } from '@/hooks/useUpdateEffect';
import { IWishlist, useGetWishlistData, useGetWishlistRentals } from '@/hooks/useWishlists';
import { getUserCurrency } from '@/redux/selectors/currency';
import { getUserFavorites } from '@/redux/selectors/favorites';
import { getLocale } from '@/redux/selectors/locale';
import { IData, IMeta, ISearchRentals } from '@/services/types/search/rentals/id';
import { mapRentalsToTiles } from '@/utility/mapSearchResultToTile';

interface WishlistContextType {
  wishlistRentals: ISearchRentals | null;
  wishlistData: IWishlist | undefined;
  isWishlistDataLoading: boolean;
  requiresLogin: boolean;
  error: Error | null;
  isFetched: boolean;
  fetchWishlistRentals: (rentalIds: number[]) => void;
  filteredRentalsResponse: ISearchRentals | null | undefined;
  filteredRentals: IData[];
  isFilteredRentalsLoading: boolean;
  refetchWishlistData: () => Promise<IWishlist | undefined>;
  handleAddFavorite: (rental: IData, index: number) => void;
  handleRemoveFavorite: (rental: IData, index: number) => void;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export const WishlistProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { id } = useParams();
  const [wishlistRentals, setWishlistRentals] = useState<ISearchRentals | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isFetched, setIsFetched] = useState(false);
  const [currentRentalIds, setCurrentRentalIds] = useState<number[]>([]);
  const favorites = useSelector(getUserFavorites);

  const {
    data: wishlistData,
    mutate: refetchWishlistData,
    isValidating: isWishlistDataLoading,
    error: wishlistDataError,
  } = useGetWishlistData(Number(id));

  // Flag to prevent redundant empty state processing
  const hasProcessedEmptyState = useRef(false);

  // Flag to skip initial effect execution
  const isInitialMount = useRef(true);

  const {
    data: filteredRentalsResponse,
    isValidating: isFilteredRentalsLoading,
    mutate: refetchFilteredRentals,
  } = useGetWishlistRentals({
    rentalIds: wishlistData?.rentals || [],
    from: wishlistData?.start_date,
    to: wishlistData?.end_date,
  });

  const filteredRentals = filteredRentalsResponse?.data || [];

  const currency = useSelector(getUserCurrency);
  const locale = useSelector(getLocale);

  const { data, error: fetchError } = useGetWishlistRentals(
    currentRentalIds.length === 0
      ? { rentalIds: [], currency, locale: locale.locale }
      : { rentalIds: currentRentalIds, currency, locale: locale.locale },
  );

  const handleAddFavorite = async (rental: IData) => {
    const rentalTile = mapRentalsToTiles({
      rentals: [rental],
      favorites,
      hasImageCarousel: true,
      queryParams: {},
    })[0];
    if (rentalTile) {
      await refetchWishlistData();
    }
  };

  const handleRemoveFavorite = async (rental: IData) => {
    const rentalTile = mapRentalsToTiles({
      rentals: [rental],
      favorites,
      hasImageCarousel: true,
      queryParams: {},
    })[0];
    if (rentalTile) {
      await refetchWishlistData();
    }
  };

  const fetchWishlistRentals = useCallback(
    (rentalIds: number[]) => {
      if (rentalIds.length > 0 && JSON.stringify(rentalIds) !== JSON.stringify(currentRentalIds)) {
        hasProcessedEmptyState.current = false;
        setCurrentRentalIds(rentalIds);
      }
    },
    [currentRentalIds],
  );

  // Process currentRentalIds changes and data updates
  useUpdateEffect(() => {
    if (!currentRentalIds.length) {
      // Only process once to avoid loop
      if (!hasProcessedEmptyState.current) {
        hasProcessedEmptyState.current = true;
        setWishlistRentals({ data: [], meta: {} as IMeta });
        setIsFetched(true);
      }
      return;
    }

    // Only update when we have actual data response
    if (data && data.data) {
      setWishlistRentals(data);
      setIsFetched(true);
    }

    // Handle errors
    if (fetchError) {
      setError(fetchError);
    }
  }, [data, currentRentalIds, fetchError]);

  useUpdateEffect(() => {
    // Skip first run to avoid initialization loops
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Process rental IDs when wishlist data changes
    if (wishlistData?.rentals && wishlistData.rentals.length > 0) {
      fetchWishlistRentals(wishlistData.rentals);
    } else if (wishlistData && (!wishlistData.rentals || wishlistData.rentals.length === 0)) {
      // Only process empty state once to avoid loop
      if (!hasProcessedEmptyState.current) {
        hasProcessedEmptyState.current = true;
        setCurrentRentalIds([]);
        setWishlistRentals({ data: [], meta: {} as IMeta });
        setIsFetched(true);
      }
    }
  }, [wishlistData, fetchWishlistRentals]);

  // Add effect to watch favorites changes and trigger refetch
  useUpdateEffect(() => {
    if (!isInitialMount.current) {
      refetchWishlistData();
    }
  }, [favorites, refetchWishlistData, refetchFilteredRentals]);

  const value = {
    wishlistRentals,
    wishlistData,
    isWishlistDataLoading,
    requiresLogin: wishlistDataError?.status === 403,
    error,
    isFetched,
    fetchWishlistRentals,
    filteredRentalsResponse,
    filteredRentals,
    isFilteredRentalsLoading,
    refetchWishlistData,
    handleAddFavorite,
    handleRemoveFavorite,
  };

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (context === undefined) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};
